import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
  Checkbox,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { getProyectosApiBaseUrl } from "../../utils/proyectosApiBaseUrl";

const getToken = () => {
  try {
    return localStorage.getItem("token") || "";
  } catch {
    return "";
  }
};

export type InformeAvanceObraCargado = {
  id?: string;
  titulo?: string;
  tipo_documento?: string;
  documento_id?: number;
  correo_destino?: string;
  correo_enviado_at?: string;
  cargado_at?: string;
  cargado_por?: string;
  usar_correo_default?: boolean;
};

type FilaBorrador = {
  id: string;
  titulo: string;
  usarCorreoDefault: boolean;
  correoDestino: string;
  archivo: File | null;
};

type Props = {
  proyectoId: number;
  workflow: Record<string, any>;
  documentosPorTipo: Record<string, { id?: number; nombre_archivo?: string } | undefined>;
  puedeGestionar: boolean;
  onExito: () => void;
  onVisualizar: (tipo: string) => void;
  guardando: boolean;
};

function nuevaFilaBorrador(): FilaBorrador {
  return {
    id: `inf${Date.now()}${Math.random().toString(36).slice(2, 6)}`,
    titulo: "",
    usarCorreoDefault: true,
    correoDestino: "",
    archivo: null,
  };
}

export default function InformeAvanceObraPanel({
  proyectoId,
  workflow,
  documentosPorTipo,
  puedeGestionar,
  onExito,
  onVisualizar,
  guardando,
}: Props) {
  const [correoDefault, setCorreoDefault] = React.useState(
    String(workflow?.correo_mantenimiento_default || ""),
  );
  const [guardandoCorreo, setGuardandoCorreo] = React.useState(false);
  const [subiendoFila, setSubiendoFila] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [ok, setOk] = React.useState<string | null>(null);
  const [filas, setFilas] = React.useState<FilaBorrador[]>([nuevaFilaBorrador()]);

  React.useEffect(() => {
    setCorreoDefault(String(workflow?.correo_mantenimiento_default || ""));
  }, [workflow?.correo_mantenimiento_default]);

  const informesCargados: InformeAvanceObraCargado[] = Array.isArray(workflow?.informes)
    ? workflow.informes
    : [];

  const guardarCorreoDefault = async () => {
    try {
      setGuardandoCorreo(true);
      setError(null);
      setOk(null);
      const res = await fetch(`${getProyectosApiBaseUrl()}/${proyectoId}/informe-avance-obra/config`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ correo_mantenimiento_default: correoDefault.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "No se pudo guardar el correo.");
      setOk("Correo de mantenimiento guardado.");
      onExito();
    } catch (e: any) {
      setError(e.message || "Error al guardar correo.");
    } finally {
      setGuardandoCorreo(false);
    }
  };

  const subirFila = async (fila: FilaBorrador) => {
    if (!fila.archivo) {
      setError("Seleccione un archivo para esta fila.");
      return;
    }
    if (!fila.titulo.trim()) {
      setError("Indique un título o descripción para el informe.");
      return;
    }
    if (!fila.usarCorreoDefault && !fila.correoDestino.trim()) {
      setError("Indique el correo destino o marque «usar mismo correo de mantenimiento».");
      return;
    }
    try {
      setSubiendoFila(fila.id);
      setError(null);
      setOk(null);
      const fd = new FormData();
      fd.append("archivo", fila.archivo);
      fd.append("titulo", fila.titulo.trim());
      fd.append("fila_id", fila.id);
      fd.append("usar_correo_default", fila.usarCorreoDefault ? "true" : "false");
      if (!fila.usarCorreoDefault) {
        fd.append("correo_destino", fila.correoDestino.trim());
      }
      const res = await fetch(`${getProyectosApiBaseUrl()}/${proyectoId}/informe-avance-obra`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "No se pudo cargar el informe.");
      setOk(`Informe cargado y notificado a ${data.correo_destino || "mantenimiento"}.`);
      setFilas((prev) => {
        const rest = prev.filter((f) => f.id !== fila.id);
        return rest.length ? rest : [nuevaFilaBorrador()];
      });
      onExito();
    } catch (e: any) {
      setError(e.message || "Error al subir informe.");
    } finally {
      setSubiendoFila(null);
    }
  };

  return (
    <Grid container spacing={1.5}>
      <Grid item xs={12}>
        <Alert severity="info">
          Cargue aquí los <strong>informes de avance de obra</strong> que pasarán a{" "}
          <strong>mantenimiento / ejecución de obras</strong>. Por cada documento se envía un correo al
          responsable con el archivo adjunto. Puede usar un correo único o uno distinto por informe.
        </Alert>
      </Grid>
      {error && (
        <Grid item xs={12}>
          <Alert severity="error">{error}</Alert>
        </Grid>
      )}
      {ok && (
        <Grid item xs={12}>
          <Alert severity="success">{ok}</Alert>
        </Grid>
      )}
      <Grid item xs={12}>
        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            Correo responsable mantenimiento (por defecto)
          </Typography>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }}>
            <TextField
              id="informe-avance-correo-default"
              name="correo_mantenimiento_default"
              label="Correo mantenimiento"
              type="email"
              size="small"
              fullWidth
              value={correoDefault}
              onChange={(e) => setCorreoDefault(e.target.value)}
              disabled={!puedeGestionar || guardando}
              helperText="Se usa cuando cada fila indica «mismo correo»"
            />
            <Button
              variant="outlined"
              disabled={!puedeGestionar || guardandoCorreo || guardando || !correoDefault.trim()}
              onClick={() => void guardarCorreoDefault()}
            >
              {guardandoCorreo ? "Guardando..." : "Guardar correo"}
            </Button>
          </Stack>
        </Paper>
      </Grid>

      {informesCargados.length > 0 && (
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Informes ya enviados a mantenimiento
            </Typography>
            <Stack spacing={1}>
              {informesCargados.map((inf, idx) => {
                const tipo = String(inf.tipo_documento || "");
                const doc = tipo ? documentosPorTipo[tipo] : undefined;
                return (
                  <Paper key={`${tipo}-${idx}`} variant="outlined" sx={{ p: 1, backgroundColor: "#fafafa" }}>
                    <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography fontWeight={600}>{inf.titulo || "Informe avance de obra"}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Notificado a: {inf.correo_destino || "—"}
                          {inf.correo_enviado_at
                            ? ` · ${new Date(inf.correo_enviado_at).toLocaleString()}`
                            : ""}
                        </Typography>
                      </Box>
                      <Chip size="small" color={doc?.id ? "success" : "default"} label={doc?.id ? "Cargado" : "Sin archivo"} />
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!doc?.id || guardando}
                        onClick={() => tipo && onVisualizar(tipo)}
                      >
                        Ver
                      </Button>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          </Paper>
        </Grid>
      )}

      <Grid item xs={12}>
        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              Agregar informes
            </Typography>
            <Button
              size="small"
              variant="outlined"
              disabled={!puedeGestionar || guardando}
              onClick={() => setFilas((prev) => [...prev, nuevaFilaBorrador()])}
            >
              Agregar fila
            </Button>
          </Stack>
          <Stack spacing={1.5}>
            {filas.map((fila, idx) => (
              <Paper key={fila.id} variant="outlined" sx={{ p: 1.2 }}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <TextField
                      id={`informe-titulo-${fila.id}`}
                      name={`informe_titulo_${fila.id}`}
                      label={`Título informe ${idx + 1}`}
                      size="small"
                      fullWidth
                      value={fila.titulo}
                      disabled={!puedeGestionar}
                      onChange={(e) =>
                        setFilas((prev) =>
                          prev.map((f) => (f.id === fila.id ? { ...f, titulo: e.target.value } : f)),
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={fila.usarCorreoDefault}
                          disabled={!puedeGestionar}
                          onChange={(e) =>
                            setFilas((prev) =>
                              prev.map((f) =>
                                f.id === fila.id ? { ...f, usarCorreoDefault: e.target.checked } : f,
                              ),
                            )
                          }
                        />
                      }
                      label="Mismo correo de mantenimiento"
                    />
                    {!fila.usarCorreoDefault && (
                      <TextField
                        id={`informe-correo-${fila.id}`}
                        name={`informe_correo_${fila.id}`}
                        label="Correo para este informe"
                        type="email"
                        size="small"
                        fullWidth
                        sx={{ mt: 0.5 }}
                        value={fila.correoDestino}
                        disabled={!puedeGestionar}
                        onChange={(e) =>
                          setFilas((prev) =>
                            prev.map((f) =>
                              f.id === fila.id ? { ...f, correoDestino: e.target.value } : f,
                            ),
                          )
                        }
                      />
                    )}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    {puedeGestionar ? (
                      <input
                        id={`informe-archivo-${fila.id}`}
                        name={`informe_archivo_${fila.id}`}
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg,.awd,.xls,.xlsx,.doc,.docx"
                        aria-label={`Archivo informe ${idx + 1}`}
                        onChange={(e) =>
                          setFilas((prev) =>
                            prev.map((f) =>
                              f.id === fila.id ? { ...f, archivo: e.target.files?.[0] || null } : f,
                            ),
                          )
                        }
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Sin permiso de carga.
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Button
                        size="small"
                        variant="contained"
                        disabled={
                          !puedeGestionar ||
                          guardando ||
                          subiendoFila === fila.id ||
                          !fila.archivo
                        }
                        onClick={() => void subirFila(fila)}
                      >
                        {subiendoFila === fila.id ? "Subiendo..." : "Subir y notificar"}
                      </Button>
                      {filas.length > 1 && (
                        <IconButton
                          size="small"
                          aria-label="Eliminar fila"
                          disabled={!puedeGestionar || guardando || subiendoFila !== null}
                          onClick={() =>
                            setFilas((prev) => prev.filter((f) => f.id !== fila.id))
                          }
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Stack>
        </Paper>
      </Grid>
    </Grid>
  );
}
